import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { tokenBalance, useQuotaQuery } from '@entities/Plan';
import { paths } from '@shared/config';
import { cn } from '@shared/lib';
import { TokenIcon } from '@shared/ui';
import { formatResetTime } from '../lib/resetTime';

/**
 * Баланс жетонов на сегодня (макет «Создание карточек»): «Осталось N из M», полоса —
 * доля остатка, в полночь полная и пустеет с каждой тратой (решение владельца
 * 2026-10-13; до этого показывали потраченное). Мало осталось — жёлтая, ноль — пустая,
 * время прихода новых и ссылка на тарифы. Несгораемые жетоны (с 2026-10-22) — «+N» рядом:
 * дневные кончились, а несгораемые есть — генерация идёт дальше.
 */
export function QuotaMeter({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { data: quota } = useQuotaQuery();
  if (!quota) return null;

  const b = tokenBalance(quota);
  const exhausted = b.out;
  const title = [t('quota.balanceTitle', { left: b.left, limit: b.limit }), b.bonus > 0 ? t('quota.bonusTitle', { count: b.bonus }) : '']
    .filter(Boolean)
    .join(' ');

  return (
    <div className={cn('flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[14px] text-muted', className)} title={title}>
      <span className="inline-flex items-center gap-1.5">
        <TokenIcon size={16} />
        {t('quota.label')}
        <span className={cn('font-medium', exhausted && b.bonus <= 0 ? 'text-[#f47a7e]' : 'text-fg')}>{b.left}</span>
        {t('quota.outOf', { limit: b.limit })}
      </span>
      <span className="block h-1 w-20 overflow-hidden rounded-sm bg-line" aria-hidden>
        <span
          className={cn('block h-full rounded-sm transition-[width] duration-500', b.low ? 'bg-warn' : 'bg-accent')}
          style={{ width: `${(b.share * 100).toFixed(1)}%` }}
        />
      </span>
      {b.bonus > 0 && <span className="font-medium text-accent">{t('quota.bonus', { count: b.bonus })}</span>}
      {exhausted &&
        (b.bonus > 0 ? (
          <span>· {t('quota.bonusSpending')}</span>
        ) : (
          <>
            <span>· {t('quota.resetAt', { time: formatResetTime(quota.resetAt) })}</span>
            <Link to={paths.plans} className="font-medium text-accent underline-offset-2 hover:underline">
              {t('quota.upgrade')}
            </Link>
          </>
        ))}
    </div>
  );
}
